<template>
  <form ref="formTipoNota" @submit.prevent="guardar">
    <InputCsrf />
    <div class="row">
      <div class="col-sm-6">
        <input-base
          ref="inputNombre"
          v-model="tipo_nota.nombre"
          :error="errores.nombre"
          :label-key="$I18n.t('activerecord.attributes.calificaciones/tipo_nota.nombre')"
          :required="true"
        />
      </div>
      <div
        class="col-sm-4"
        :data-toggle="tipo_nota.tiene_notas ? 'tooltip' : ''"
        :title="tipo_nota.tiene_notas ? $I18n.t('tipo_nota_errores.tiene_notas') : ''"
      >
        <label for="" class="input-label">
          <abbr title="requerido">*</abbr>
          {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.tipo_nota') }}
        </label>
        <select
          v-model="tipo_nota.tipo_nota"
          class="form-control"
          :disabled="tipo_nota.tiene_notas"
        >
          <option
            v-for="option in tiposNotas"
            :key="option.value"
            :value="option.value"
          >
            {{ option.name }}
          </option>
        </select>
        <p v-if="errores.tipo_nota" class="text-danger">
          {{ modelErrorsToHtmlText(errores.tipo_nota) }}
        </p>
      </div>
      <div class="col-sm-2 m-t-lg">
        <input-checkbox-boolean v-model="tipo_nota.activa">
          {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.activa') }}
        </input-checkbox-boolean>
      </div>
    </div>
    <div class="form-group">
      <input-base
        ref="inputDescripcion"
        v-model="tipo_nota.descripcion"
        :error="errores.descripcion"
        :label-key="$I18n.t('activerecord.attributes.calificaciones/tipo_nota.descripcion')"
        :required="false"
      />
    </div>
    <transition enter-active-class="animated fadeIn">
      <div v-if="isNumerica()" class="row">
        <div class="col-sm-3">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.tipo_numero') }}
          </label>
          <select v-model="tipo_nota.tipo_numero" class="form-control">
            <option
              v-for="option in tiposNumero"
              :key="option.value"
              :value="option.value"
            >
              {{ option.name }}
            </option>
          </select>
          <p v-if="errores.tipo_numero" class="text-danger">
            {{ modelErrorsToHtmlText(errores.tipo_numero) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.minimo') }}
          </label>
          <inputNumerico
            name="minimo"
            :configuracion="inputNumericoConfig()"
            :model-value="parseNumero(tipo_nota.minimo)"
            @update:model-value="tipo_nota.minimo = $event"
          />
          <p v-if="errores.minimo" class="text-danger">
            {{ modelErrorsToHtmlText(errores.minimo) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            <abbr title="requerido">*</abbr>
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.maximo') }}
          </label>
          <inputNumerico
            name="maximo"
            :configuracion="inputNumericoConfig()"
            :model-value="parseNumero(tipo_nota.maximo)"
            @update:model-value="tipo_nota.maximo = $event"
          />
          <p v-if="errores.maximo" class="text-danger">
            {{ modelErrorsToHtmlText(errores.maximo) }}
          </p>
        </div>
        <div class="col-sm-2">
          <label for="" class="input-label">
            {{ $I18n.t('aprobado') }}
          </label>
          <inputNumerico
            v-model="tipo_nota.aprobado"
            name="aprobado"
            :configuracion="inputNumericoConfig()"
          />
          <p v-if="errores.aprobado" class="text-danger">
            {{ modelErrorsToHtmlText(errores.aprobado) }}
          </p>
        </div>
        <div class="col-sm-3">
          <input-checkbox-boolean v-model="tipo_nota.porcentaje" class="m-t-label">
            {{ $I18n.t('activerecord.attributes.calificaciones/tipo_nota.porcentaje') }}
          </input-checkbox-boolean>
        </div>
      </div>
    </transition>
    <div class="m-t-lg">
      <opciones-form
        :model-value="opciones"
        :error="errores.opciones"
        :tipo-nota="tipo_nota.tipo_nota"
        @update:model-value="tipo_nota.opciones=$event"
      />
    </div>
    <div class="form-actions m-t-lg">
      <a v-if="cancelarUrl" class="btn btn-default" :href="cancelarUrl">
        <i class="fa fa-angle-left"></i> {{ $I18n.t('Cancelar') }}
      </a>
      <boton-guardar :submitting="guardando"></boton-guardar>
    </div>
  </form>
</template>

<script>
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import axios from 'axios';
import { redirectTo } from '@/libs/cifra/urls';
import { getModelJsonErrors, modelErrorsToHtmlText } from '../../../../libs/cifra/model_errors';
import OpcionesForm from './OpcionesForm.vue';

export default {
  components: { OpcionesForm },
  props: {
    model: { type: Object, required: true },
    cancelarUrl: { type: String, default: null },
  },
  data() {
    const tipoNota = ({ ...cloneDeep(this.model) }) || {
      nombre: null,
      tipo_nota: 'numerica',
      descripcion: null,
      activa: false,
      porcentaje: false,
      tiene_notas: false,
    };
    return {
      tipo_nota: tipoNota,
      guardando: false,
      errores: {},
      tiposNotas: [
        { name: this.$I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_nota.numerica'), value: 'numerica' },
        { name: this.$I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_nota.texto'), value: 'texto' }
      ],
      tiposNumero: [
        { name: this.$I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_numero.entero'), value: 'entero' },
        { name: this.$I18n.t('activerecord.enums.calificaciones/tipo_nota.tipo_numero.decimal'), value: 'decimal' },
      ],
    };
  },
  computed: {
    opciones() {
      return sortBy(this.tipo_nota.opciones || [], (o) => o.orden);
    },
  },
  watch: {
    'tipo_nota.tipo_nota': function (valor) {
      const numerico = this.tiposNotas[0].value;
      if (valor === numerico) {
        this.tipo_nota.minimo = this.parseNumero(this.model.minimo);
        this.tipo_nota.maximo = this.parseNumero(this.model.maximo);
        this.tipo_nota.aprobado = this.parseNumero(this.model.aprobado);
        this.tipo_nota.opciones = [];
      } else {
        // Para texto
        this.tipo_nota.minimo = null;
        this.tipo_nota.maximo = null;
        this.tipo_nota.aprobado = null;
        this.tipo_nota.tipo_numero = null;
        this.tipo_nota.porcentaje = false;
      }
    },
  },
  mounted() {
    this.$refs.inputNombre.focus();
  },
  methods: {
    decimal() {
      return this.tiposNumero[1].value;
    },
    isNumerica() {
      // Solo mostrar campos numéricos para tipo numerica
      return this.tipo_nota.tipo_nota === this.tiposNotas[0].value;
    },
    isNumeroEntero() {
      return this.tipo_nota.tipo_numero === this.tiposNumero[0].value;
    },
    isNumeroDecimal() {
      return this.tipo_nota.tipo_numero === this.tiposNumero[1].value;
    },
    inputNumericoConfig() {
      if (this.isNumeroEntero()) {
        return 'entero';
      }
      if (this.isNumeroDecimal()) {
        return 'decimal';
      }

      return '';
    },
    parseNumero(numero) {
      if (isEmpty(numero)) {
        return null;
      }
      if (this.isNumeroEntero()) {
        return parseInt(numero || 0, 10);
      }
      return parseFloat(numero);
    },
    guardar() {
      this.guardando = true;

      let url = '/calificaciones/tipo_notas';
      let method = 'post';
      if (this.tipo_nota.id) {
        url = `${url}/${this.tipo_nota.id}`;
        method = 'patch';
      }

      this.tipo_nota.opciones_attributes = sortBy(this.tipo_nota.opciones, 'orden');

      axios({
        url,
        method,
        data: { calificaciones_tipo_nota: this.tipo_nota },
      }).then((result) => {
        const modelo = result.data;
        this.errores = {};
        this.tipo_nota = modelo;
        // Si lo acabamos de crear redireccionamos, si es edición no hacemos nada
        if (method === 'post') {
          redirectTo(`/calificaciones/tipo_notas/${modelo.id}/edit`);
        }
      }).catch((e) => {
        this.errores = getModelJsonErrors(e.response.data);
      }).then(() => {
        this.guardando = false;
      });
    },
    modelErrorsToHtmlText,
  },
};
</script>

<style scoped>
  table > tbody > tr > td:last-child {
    text-align: center;
  }
  abbr {
    border: 0;
    text-decoration: none;
    color: #fe1d12;
  }
</style>
